import type { CSSProperties } from "react";
import type { DocumentBackground, TemplateElement, TextElement, WildcardElement, WildcardKey } from "./Documents.types";
import { BACKGROUND_COMPRESSION_QUALITY, MAX_BACKGROUND_DIMENSION, WILDCARD_LABELS } from "./Documents.types";

export function isTextLike(el: TemplateElement): el is TextElement | WildcardElement {
  return el.type === "text" || el.type === "wildcard";
}

export function resolveText(
  el: TextElement | WildcardElement,
  values?: Partial<Record<WildcardKey, string>>
): string {
  if (el.type === "text") return el.content;
  if (values && values[el.wildcardKey]) return values[el.wildcardKey] as string;
  return WILDCARD_LABELS[el.wildcardKey];
}

export function elementPositionStyle(el: TemplateElement, scale: number): CSSProperties {
  return {
    position: "absolute",
    left: el.x * scale,
    top: el.y * scale,
    width: el.width * scale,
    height: el.height * scale,
    transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
    zIndex: el.zIndex,
  };
}

export function textElementStyle(el: TextElement | WildcardElement, scale: number): CSSProperties {
  return {
    fontFamily: el.fontFamily,
    fontSize: el.fontSize * scale,
    fontWeight: el.fontWeight,
    fontStyle: el.italic ? "italic" : "normal",
    color: el.color,
    textAlign: el.align,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: el.align === "left" ? "flex-start" : el.align === "right" ? "flex-end" : "center",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.2,
    userSelect: "none",
  };
}

// The background image is its own absolutely-positioned layer *behind* the
// elements, rather than a CSS background on the page container itself — that
// way `opacity` fades only the image, never the text/wildcards on top of it.
export function backgroundLayerStyle(background: DocumentBackground): CSSProperties {
  return {
    position: "absolute",
    inset: 0,
    backgroundImage: `url(${background.url})`,
    backgroundSize: background.fit === "fill" ? "100% 100%" : background.fit,
    backgroundPosition: `${background.focalPoint.x}% ${background.focalPoint.y}%`,
    backgroundRepeat: "no-repeat",
    opacity: background.opacity / 100,
  };
}

// -----------------------------------------
// PRINT (used for "download as PDF" — scopes the browser print dialog to a
// single container via injected @media print rules; no new dependency)
// -----------------------------------------

export function printElement(containerId: string, documentTitle?: string) {
  const styleId = "__doc_print_style__";
  document.getElementById(styleId)?.remove();

  const style = document.createElement("style");
  style.id = styleId;
  style.media = "print";
  style.textContent = `
    @media print {
      body * { visibility: hidden !important; }
      #${containerId}, #${containerId} * { visibility: visible !important; }
      #${containerId} { position: absolute !important; top: 0; left: 0; margin: 0; box-shadow: none !important; }
    }
  `;
  document.head.appendChild(style);

  const prevTitle = document.title;
  if (documentTitle) document.title = documentTitle;

  const cleanup = () => {
    style.remove();
    document.title = prevTitle;
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  window.print();
}

// -----------------------------------------
// CLIENT-SIDE COMPRESSION (runs on file select, before the file ever leaves
// the browser) — downscales to MAX_BACKGROUND_DIMENSION on the long edge and
// re-encodes as WebP (supports alpha, far smaller than PNG/JPEG at
// comparable quality) so most uploads land well under the byte cap without
// a visible quality hit. Falls back to the original file untouched whenever
// compressing wouldn't actually help.
// -----------------------------------------

export async function compressBackgroundImage(file: File): Promise<{ file: File; width: number; height: number }> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Couldn't read that image — please try another file."));
      el.src = objectUrl;
    });

    const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = Math.min(1, MAX_BACKGROUND_DIMENSION / longEdge);
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { file, width: img.naturalWidth, height: img.naturalHeight };
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", BACKGROUND_COMPRESSION_QUALITY)
    );

    // Keep the original whenever re-encoding didn't actually win anything —
    // no point trading quality for zero size benefit.
    if (!blob || blob.size >= file.size) {
      return { file, width: img.naturalWidth, height: img.naturalHeight };
    }

    const compressedFile = new File(
      [ blob ],
      file.name.replace(/\.\w+$/, "") + ".webp",
      { type: "image/webp" }
    );
    return { file: compressedFile, width, height };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
